import { uniqWith } from "lodash";
import {
  Institution,
  Network,
  Program,
  ProgramMembership,
  ProgramRole,
  Sponsor,
  User
} from "../API";
import { CognitoUserGroups } from "../features/Users/userUtils";
import {
  selectCurrentUser,
  selectUserCognitoGroups
} from "../features/Users/usersSlice";
import { PGYSelectFields, ProgramRoleSelectFields } from "./constants";
import { useAppSelector } from "./hooks";
import { useAllPrograms } from "../features/Programs/programUtils";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { QueryStatus } from "./types";

export const stringOfRoleOption = (programRole: ProgramRole): string => {
  const field = ProgramRoleSelectFields.find(
    (field) => field.value === programRole
  );
  return field?.label || "";
};

export const stringOfPGYOption = (pgy: number): string => {
  const pgyField = PGYSelectFields.find((field) => field.value === pgy);
  return pgyField?.label || "";
};

export const returnIdsOfProgramsThatUserAdmins = (
  user: User | undefined
): string[] => {
  const toReturn: string[] = [];
  if (!user) return toReturn;
  const usersProgramMemberships = user.programMemberships?.items;
  if (usersProgramMemberships && usersProgramMemberships.length > 0) {
    for (const programMembership of usersProgramMemberships) {
      if (programMembership?.programRole === ProgramRole.ProgramAdmin) {
        toReturn.push(programMembership.programId);
      }
    }
  }
  return toReturn;
};

export const getProgramMembershipsAsProgramAdmin = (user: User | undefined) => {
  const programMemberships = user?.programMemberships?.items;
  const programMembershipsAsProgramAdmin = programMemberships?.filter(
    (program) =>
      program &&
      program.programRole == ProgramRole.ProgramAdmin &&
      // Membership is active
      !program.endDate
  );
  const hasMultipleProgramsAsProgramAdmin =
    programMembershipsAsProgramAdmin &&
    programMembershipsAsProgramAdmin.length > 1;
  return {
    programs: programMemberships,
    programMembershipsAsProgramAdmin,
    hasMultipleProgramsAsProgramAdmin
  };
};

type UserAdministeredEntities = {
  canAdministerMultipleInstitutions: boolean;
  canAdministerMultiplePrograms: boolean;
  programs?: Program[] | null;
  institutions?: Institution[] | null;
};
export const getProgramsCurrentUserCanAdminister =
  (): UserAdministeredEntities => {
    const currentUser = useAppSelector(selectCurrentUser);
    const currentUserCognitoGroups = useAppSelector(selectUserCognitoGroups);
    const { programs } = useAllPrograms();
    return getProgramsUserCanAdminister(
      currentUser,
      currentUserCognitoGroups,
      programs
    );
  };

export const usersActionableRoleInProgram = (
  user: User,
  program: Program
): ProgramRole | undefined => {
  const usersMembershipsToProgram = user.programMemberships?.items?.filter(
    (membership) => membership?.programId === program.id
  );
  const usersRolesInProgram = usersMembershipsToProgram?.map(
    (membership) => membership?.programRole
  );
  if (usersRolesInProgram?.includes(ProgramRole.Attending)) {
    return ProgramRole.Attending;
  }
  if (usersRolesInProgram?.includes(ProgramRole.Trainee)) {
    return ProgramRole.Trainee;
  }
  return;
};

export const getProgramsUserCanAdminister = (
  user?: User,
  cognitoGroups?: CognitoUserGroups[],
  allPrograms?: Program[] | null
): UserAdministeredEntities => {
  let programs: Program[] | null | undefined = [],
    institutions: Institution[] | null | undefined = [];
  if (!!user && !!cognitoGroups && !!allPrograms) {
    if (cognitoGroups?.includes(CognitoUserGroups.SysAdmins)) {
      programs = allPrograms;
    } else if (cognitoGroups?.includes(CognitoUserGroups.ProgramAdmins)) {
      const programMemberships = user?.programMemberships?.items;
      const activeProgramMemberships = programMemberships?.filter(
        (pm) => pm && !pm.endDate
      );
      const adminProgramMemberships = activeProgramMemberships?.filter(
        (pm) => pm && pm.programRole == ProgramRole.ProgramAdmin
      );
      const adminPrograms = allPrograms.filter((p) =>
        adminProgramMemberships?.find((apm) => apm?.programId == p.id)
      );
      programs = adminPrograms;
    }
  }

  institutions = uniqWith(
    programs?.filter((p) => !!p.institution).map((p) => p.institution!),
    (a, b) => !!a && !!b && a.id == b.id
  ).sort((a, b) => {
    if (!a?.name || !b?.name) {
      return 1;
    } else if (a?.name > b?.name) {
      return 1;
    } else {
      return -1;
    }
  });

  const canAdministerMultiplePrograms = programs.length > 1;
  const canAdministerMultipleInstitutions = institutions.length > 1;

  return {
    canAdministerMultiplePrograms,
    canAdministerMultipleInstitutions,
    programs,
    institutions
  };
};

export const containsOnly = <ArrayElement>(
  fullArray: ArrayElement[],
  targetArray: ArrayElement[]
) => {
  return fullArray.every((fullArrayElement) =>
    targetArray.includes(fullArrayElement)
  );
};

export const valueWithoutUpdate = (value: any) => {
  return JSON.parse(JSON.stringify(value));
};

export const fetchUniqueSponsorsFromProgramMemberships = async (
  programMemberships: (ProgramMembership | null)[],
  getNetworksByProgram: (programId: string) => Promise<Network[] | null>
) => {
  const uniqueSponsors: Sponsor[] = [];

  for (const programMembership of programMemberships) {
    if (!programMembership) {
      console.error(
        "programMembership is not available in programMemberships list:",
        programMemberships
      );
      return uniqueSponsors;
    }
    const { programId } = programMembership;
    if (!programId) {
      console.error(
        "programId not set for programMembership:",
        programMembership
      );
      return uniqueSponsors;
    }
    const networksAssocatiatedWithProgram = await getNetworksByProgram(
      programId
    );
    if (!networksAssocatiatedWithProgram) {
      console.error("there are no networks associated with the program.");
      return uniqueSponsors;
    }

    for (const network of networksAssocatiatedWithProgram) {
      if (
        !uniqueSponsors.some((sponsor) => sponsor.id === network.sponsor?.id) &&
        network.sponsor
      ) {
        uniqueSponsors.push(network.sponsor);
      }
    }
  }
  return uniqueSponsors;
};

export const usersActiveProgramMemberships = (user: User) => {
  const membershipItems = Array.isArray(user.programMemberships?.items)
    ? user.programMemberships?.items
    : [];
  const usersProgramMemberships = membershipItems.flatMap((membership) =>
    membership ? [membership] : []
  );
  const usersActiveProgramMemberships = usersProgramMemberships?.filter(
    (membership) => membership && !membership.endDate
  );
  return usersActiveProgramMemberships;
};

export const createDashboardLinkText = (initialText: string) => {
  return `${initialText} Reports`;
};

export const getBaseEndpoint = () => {
  // 1) Explicit override (best for CI, dev tunnels, or nonstandard hosts)
  const explicit = process.env.REACT_APP_API_BASE_URL;
  if (explicit) {
    return explicit.endsWith("/") ? explicit : `${explicit}/`;
  }

  // 2) Cloud envs (production, staging, dev, etc.)
  //    REACT_APP_ENV is already in this project; use api[-ENV].simpl.org
  const envName = process.env.REACT_APP_ENV?.trim();
  if (envName && envName !== "local") {
    const host =
      envName === "production" ? "https://api.simpl.org/" : `https://api-${envName}.simpl.org/`;
    return host;
  }

  // 3) Local default
  return "http://localhost:3001/api/";
};

export const getActiveProgramAdminedProgramIds = (currentUser: User) => {
  return currentUser?.programMemberships?.items
    ?.map((programMembership) => {
      if (
        programMembership?.programRole === "ProgramAdmin" &&
        !programMembership.endDate
      ) {
        return programMembership.programId;
      }
    })
    .flatMap((programId) => (programId ? [programId] : []));
};

export function useAPI<TInput, TOutput>(
  apiFunction: (input: TInput) => Promise<TOutput>,
  input?: TInput
) {
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TOutput | null>(null);

  const execute = async (input: TInput) => {
    setStatus("loading");
    setError(null);
    try {
      const results = await apiFunction(input);
      setData(results);
      setStatus("succeeded");
    } catch (error) {
      console.error(`error executing API function:`, error);
      setError(`Error fetching data.`);
      setStatus("failed");
    }
  };

  const executeDelayed = useCallback(
    debounce((input: TInput) => {
      execute(input);
    }, 500),
    []
  );

  useEffect(() => {
    if (input) {
      executeDelayed(input);
    }
  }, [input]);

  return {
    data,
    status,
    error,
    execute
  };
}